import {
  useCallback,
  useEffect,
  useState,
} from 'react';

import {
  Box,
  Stack,
  Typography
} from '@mui/material';

import { useParams } from 'react-router-dom';

import { useNotification } from '../../../../hooks/NotificationContext';

import { generateNotificationUrl } from '../../../../lib/notificationUtils';

import { formatCommentWithAccountIds } from '../../../../lib/utils';

import {
  addComment,
  getComments,
} from '../../../../modules/entity-details';

import DetailsCard from '../../../../ui/layout/DetailsCard';

import SkeletonLoader from '../../SkeletonLoader';

import CommentInput from './CommentInput';

import SingleComment from './SingleComment';
import CustomAvatar from '../../CustomAvatar';
import ActionButton from '../../../../ui/primitives/ActionButton';

const Comments = ({
  entityType,
  entityId,
  users,
  entityKey,
  otherData,
}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] =
    useState(true);

  const [comment, setComment] =
    useState('');

  const [error, setError] =
    useState(null);

  const [sort, setSort] = useState(
    'Newest First',
  );

  const [taggedUsers, setTaggedUsers] =
    useState([]);

  const { addNotification } =
    useNotification();

  const currentUser = JSON.parse(
    localStorage.getItem('user'),
  );

  const { projectId } = useParams();

  const fetchData = async () => {
    setLoading(true);

    try {
      const response = await getComments(
        entityType,
        entityId,
        projectId,
      );

      setData(response.data);

      setError(null);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const createComment = async () => {
    const mentions = [
      ...new Set(taggedUsers),
    ];

    const commentBody =
      formatCommentWithAccountIds(
        comment,
        users,
      );

    const notificationLink =
      generateNotificationUrl(
        entityType,
        projectId,
        entityKey,
        'comments',
        otherData,
      );

    const formData = {
      comment: {
        body: commentBody.trim(),
        entityType,
        entityId,
        mentions,
        additionalData: {
          commentorName:
            currentUser?.first_name +
            ' ' +
            currentUser?.last_name,

          entityKey,
          entityLink:
            notificationLink,
        },
      },
    };

    try {
      await addComment(
        formData,
        projectId,
      );

      await fetchData();

      addNotification(
        'success',
        'Comment added successfully',
      );

      setComment('');
      setTaggedUsers([]);
    } catch (error) {
      addNotification(
        'error',
        error.message ||
          'Failed to add comment',
      );
    }
  };

  useEffect(() => {
    if (entityId) {
      fetchData();
    }

    return () => {
      setData([]);
    };
  }, [entityId]);

  const sortedData = useCallback(() => {
    if (sort === 'Newest First') {
      return [...data].sort(
        (a, b) =>
          new Date(b.created_at) -
          new Date(a.created_at),
      );
    }

    return [...data].sort(
      (a, b) =>
        new Date(a.created_at) -
        new Date(b.created_at),
    );
  }, [data, sort]);

  const finalData = sortedData();

  const cardActions =
    finalData.length > 1 ? (
      <Box>
        <select
          value={sort}
          onChange={(e) =>
            setSort(e.target.value)
          }
          style={{
            padding: '8px 12px',
            borderRadius: 8,
            border:
              '1px solid rgba(0,0,0,0.2)',
          }}
        >
          <option>
            Newest First
          </option>

          <option>
            Oldest First
          </option>
        </select>
      </Box>
    ) : null;

  return (
    <DetailsCard
      title="Comments"
      actions={cardActions}
    >
      <Box>
        {finalData.length === 0 && (
          <Box>
            <Typography align="center">
              {loading ? (
                <SkeletonLoader rowCount={2} />
              ) : (
                'No comment added yet'
              )}
            </Typography>

            <Box
              sx={{
                borderBottom:
                  '1px solid',
                borderColor:
                  'divider',
                my: 2,
              }}
            />
          </Box>
        )}

        {finalData.length > 0 && (
          <Stack spacing={3}>
            {finalData.map((com) => (
              <SingleComment
                key={com.id}
                comment={com}
                fetchData={fetchData}
                users={users}
                entityKey={entityKey}
                otherData={otherData}
              />
            ))}
          </Stack>
        )}
      </Box>

      <Box sx={{ mt: 3 }}>
        <Stack
          direction="row"
          spacing={2}
          alignItems="flex-start"
        >
          <CustomAvatar
            firstName={
              currentUser?.first_name
            }
            lastName={
              currentUser?.last_name
            }
            size={32}
          />

          <Box sx={{ flex: 1 }}>
            <CommentInput
              users={users}
              comment={comment}
              onCommentChange={
                setComment
              }
              setTaggedUsers={
                setTaggedUsers
              }
            />

            {comment && (
              <Stack
                direction="row"
                spacing={1}
                justifyContent="flex-end"
                sx={{ mt: 2 }}
              >
                <ActionButton
                  label="Add"
                  intent="primary"
                  size="small"
                  onClick={
                    createComment
                  }
                />

                <ActionButton
                  label="Cancel"
                  intent="secondary"
                  size="small"
                  onClick={() =>
                    setComment('')
                  }
                />
              </Stack>
            )}
          </Box>
        </Stack>
      </Box>
    </DetailsCard>
  );
};

export default Comments;